// =============================================================================
// Origin UI — src/pages/home/HomePage.tsx
// Phase 5 — Home Screen & Module Launcher
// Full-width layout (no sidebar). Official Origin logo in banner.
// User avatar + logout in top-right corner.
// =============================================================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { dashboardApi } from '@/services/dashboardApi';
import type {
  DashboardResponse,
  DashboardQuickAction,
  DashboardAttentionItem,
  DashboardModule,
  DashboardSnapshot,
} from '@/types/api';
import { complianceApi } from '@/services/complianceApi';
import type { ComplianceOutstandingItem } from '@/services/complianceApi';
import { api } from '@/services/apiService';
import { HelpChatWidget } from '@/components/HelpChatWidget';
import styles from './home.module.css';

// ─── Quick Action SVG Icons ───

const qaIcons: Record<string, JSX.Element> = {
  'my-requests': (
    <svg viewBox="0 0 64 64" fill="none">
      <rect x="12" y="16" width="40" height="36" rx="6" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5"/>
      <rect x="18" y="22" width="28" height="4" rx="2" fill="rgba(255,255,255,0.3)"/>
      <rect x="18" y="30" width="20" height="3" rx="1.5" fill="rgba(255,255,255,0.2)"/>
      <rect x="18" y="37" width="24" height="3" rx="1.5" fill="rgba(255,255,255,0.2)"/>
      <circle cx="14" cy="10" r="5" fill="rgba(255,255,255,0.2)"/>
      <circle cx="14" cy="10" r="3" fill="rgba(255,255,255,0.35)"/>
    </svg>
  ),
  'new-request': (
    <svg viewBox="0 0 64 64" fill="none">
      <rect x="12" y="16" width="40" height="36" rx="6" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5"/>
      <rect x="18" y="22" width="28" height="4" rx="2" fill="rgba(255,255,255,0.3)"/>
      <rect x="18" y="30" width="20" height="3" rx="1.5" fill="rgba(255,255,255,0.2)"/>
      <rect x="18" y="37" width="24" height="3" rx="1.5" fill="rgba(255,255,255,0.2)"/>
      <circle cx="46" cy="14" r="12" fill="rgba(255,255,255,0.25)"/>
      <line x1="46" y1="8" x2="46" y2="20" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="40" y1="14" x2="52" y2="14" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/>
    </svg>
  ),
  'approve-pending': (
    <svg viewBox="0 0 64 64" fill="none">
      <rect x="8" y="12" width="48" height="40" rx="8" fill="rgba(45,204,211,0.15)" stroke="rgba(45,204,211,0.5)" strokeWidth="1.5"/>
      <rect x="14" y="20" width="36" height="10" rx="4" fill="rgba(45,204,211,0.12)" stroke="rgba(255,255,255,0.25)" strokeWidth="1"/>
      <circle cx="20" cy="25" r="3" fill="rgba(255,255,255,0.4)"/>
      <rect x="26" y="23" width="18" height="3" rx="1.5" fill="rgba(255,255,255,0.3)"/>
      <rect x="14" y="35" width="36" height="10" rx="4" fill="rgba(45,204,211,0.12)" stroke="rgba(255,255,255,0.25)" strokeWidth="1"/>
      <circle cx="20" cy="40" r="3" fill="rgba(255,255,255,0.4)"/>
      <rect x="26" y="38" width="14" height="3" rx="1.5" fill="rgba(255,255,255,0.3)"/>
      <circle cx="50" cy="25" r="6" fill="rgba(45,204,211,0.3)" stroke="#2DCCD3" strokeWidth="1.5"/>
      <path d="M47.5 25l2 2 3-3.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  'book-leave': (
    <svg viewBox="0 0 64 64" fill="none">
      <rect x="10" y="10" width="44" height="44" rx="10" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5"/>
      <rect x="10" y="10" width="44" height="14" rx="10" fill="rgba(255,255,255,0.2)"/>
      <rect x="10" y="20" width="44" height="4" rx="0" fill="rgba(255,255,255,0.2)"/>
      <line x1="21" y1="10" x2="21" y2="6" stroke="rgba(255,255,255,0.6)" strokeWidth="2" strokeLinecap="round"/>
      <line x1="43" y1="10" x2="43" y2="6" stroke="rgba(255,255,255,0.6)" strokeWidth="2" strokeLinecap="round"/>
      <circle cx="20" cy="33" r="3.5" fill="rgba(255,255,255,0.15)"/>
      <circle cx="32" cy="33" r="3.5" fill="rgba(255,255,255,0.15)"/>
      <circle cx="44" cy="33" r="3.5" fill="rgba(255,255,255,0.35)"/>
      <circle cx="20" cy="45" r="3.5" fill="rgba(255,255,255,0.15)"/>
      <circle cx="32" cy="45" r="3.5" fill="rgba(255,255,255,0.15)"/>
      <circle cx="44" cy="45" r="3.5" fill="rgba(255,255,255,0.15)"/>
      <circle cx="44" cy="33" r="6" fill="none" stroke="#fff" strokeWidth="1.5" strokeOpacity="0.6"/>
    </svg>
  ),
  'my-profile': (
    <svg viewBox="0 0 64 64" fill="none">
      <circle cx="32" cy="20" r="11" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.45)" strokeWidth="1.5"/>
      <circle cx="32" cy="18" r="5" fill="rgba(255,255,255,0.3)"/>
      <path d="M12 54c0-8 8-14 20-14s20 6 20 14" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5"/>
      <rect x="22" y="44" width="20" height="3" rx="1.5" fill="rgba(255,255,255,0.25)"/>
      <rect x="26" y="50" width="12" height="2" rx="1" fill="rgba(255,255,255,0.15)"/>
      <circle cx="48" cy="16" r="4" fill="rgba(255,255,255,0.25)"/>
      <path d="M46.5 16l1.2 1.2 2.3-2.4" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  'team-view': (
    <svg viewBox="0 0 64 64" fill="none">
      <circle cx="22" cy="18" r="8" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5"/>
      <circle cx="22" cy="16" r="3.5" fill="rgba(255,255,255,0.3)"/>
      <path d="M14 30c0-3 3-5.5 8-5.5s8 2.5 8 5.5" fill="rgba(255,255,255,0.1)" stroke="rgba(255,255,255,0.35)" strokeWidth="1.2"/>
      <circle cx="42" cy="18" r="8" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5"/>
      <circle cx="42" cy="16" r="3.5" fill="rgba(255,255,255,0.3)"/>
      <path d="M34 30c0-3 3-5.5 8-5.5s8 2.5 8 5.5" fill="rgba(255,255,255,0.1)" stroke="rgba(255,255,255,0.35)" strokeWidth="1.2"/>
      <circle cx="32" cy="38" r="8" fill="rgba(255,255,255,0.2)" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5"/>
      <circle cx="32" cy="36" r="3.5" fill="rgba(255,255,255,0.35)"/>
      <path d="M24 50c0-3 3-5.5 8-5.5s8 2.5 8 5.5" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.4)" strokeWidth="1.2"/>
      <line x1="22" y1="26" x2="28" y2="32" stroke="rgba(255,255,255,0.3)" strokeWidth="1" strokeDasharray="2 2"/>
      <line x1="42" y1="26" x2="36" y2="32" stroke="rgba(255,255,255,0.3)" strokeWidth="1" strokeDasharray="2 2"/>
    </svg>
  ),
  'my-tasks': (
    <svg viewBox="0 0 64 64" fill="none">
      <rect x="10" y="10" width="44" height="44" rx="8" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5"/>
      <rect x="16" y="18" width="32" height="8" rx="3" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.25)" strokeWidth="1"/>
      <circle cx="22" cy="22" r="2.5" fill="rgba(255,255,255,0.4)"/>
      <rect x="28" y="20.5" width="16" height="3" rx="1.5" fill="rgba(255,255,255,0.3)"/>
      <rect x="16" y="30" width="32" height="8" rx="3" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.25)" strokeWidth="1"/>
      <circle cx="22" cy="34" r="2.5" fill="rgba(255,255,255,0.4)"/>
      <rect x="28" y="32.5" width="12" height="3" rx="1.5" fill="rgba(255,255,255,0.3)"/>
      <rect x="16" y="42" width="32" height="8" rx="3" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.25)" strokeWidth="1"/>
      <circle cx="22" cy="46" r="2.5" fill="rgba(255,255,255,0.4)"/>
      <rect x="28" y="44.5" width="14" height="3" rx="1.5" fill="rgba(255,255,255,0.3)"/>
    </svg>
  ),
  'generate-letter': (
    <svg viewBox="0 0 64 64" fill="none">
      <rect x="14" y="8" width="36" height="48" rx="4" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5"/>
      <rect x="20" y="16" width="24" height="3" rx="1.5" fill="rgba(255,255,255,0.35)"/>
      <rect x="20" y="23" width="20" height="2" rx="1" fill="rgba(255,255,255,0.2)"/>
      <rect x="20" y="28" width="22" height="2" rx="1" fill="rgba(255,255,255,0.2)"/>
      <rect x="20" y="33" width="18" height="2" rx="1" fill="rgba(255,255,255,0.2)"/>
      <rect x="20" y="38" width="16" height="2" rx="1" fill="rgba(255,255,255,0.2)"/>
      <circle cx="46" cy="46" r="12" fill="rgba(255,255,255,0.25)"/>
      <path d="M42 46h8M46 42v8" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  'all-employees': (
    <svg viewBox="0 0 64 64" fill="none">
      <circle cx="20" cy="16" r="7" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5"/>
      <circle cx="20" cy="14.5" r="3" fill="rgba(255,255,255,0.3)"/>
      <path d="M10 30c0-4 4-7 10-7s10 3 10 7" fill="rgba(255,255,255,0.1)" stroke="rgba(255,255,255,0.35)" strokeWidth="1.2"/>
      <circle cx="44" cy="16" r="7" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5"/>
      <circle cx="44" cy="14.5" r="3" fill="rgba(255,255,255,0.3)"/>
      <path d="M34 30c0-4 4-7 10-7s10 3 10 7" fill="rgba(255,255,255,0.1)" stroke="rgba(255,255,255,0.35)" strokeWidth="1.2"/>
      <circle cx="32" cy="38" r="7" fill="rgba(255,255,255,0.2)" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5"/>
      <circle cx="32" cy="36.5" r="3" fill="rgba(255,255,255,0.35)"/>
      <path d="M22 52c0-4 4-7 10-7s10 3 10 7" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.4)" strokeWidth="1.2"/>
    </svg>
  ),
  'offers-pipeline': (
    <svg viewBox="0 0 64 64" fill="none">
      <rect x="8" y="14" width="48" height="38" rx="7" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5"/>
      <rect x="14" y="20" width="10" height="26" rx="3" fill="rgba(255,255,255,0.2)"/>
      <rect x="27" y="24" width="10" height="22" rx="3" fill="rgba(255,255,255,0.25)"/>
      <rect x="40" y="28" width="10" height="18" rx="3" fill="rgba(255,255,255,0.3)"/>
      <circle cx="19" cy="26" r="3" fill="rgba(255,255,255,0.4)"/>
      <circle cx="32" cy="30" r="3" fill="rgba(255,255,255,0.4)"/>
      <circle cx="45" cy="34" r="3" fill="rgba(255,255,255,0.4)"/>
      <path d="M22 26l5 4" stroke="rgba(255,255,255,0.35)" strokeWidth="1.2" strokeLinecap="round"/>
      <path d="M35 30l5 4" stroke="rgba(255,255,255,0.35)" strokeWidth="1.2" strokeLinecap="round"/>
    </svg>
  ),
  'compliance': (
    <svg viewBox="0 0 64 64" fill="none">
      <path d="M32 6l20 7v13c0 12-8 22-20 27-12-5-20-15-20-27V13l20-7z" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M23 31l6 6 12-13" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  'credit-cards': (
    <svg viewBox="0 0 64 64" fill="none">
      <rect x="8" y="16" width="48" height="32" rx="6" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5"/>
      <rect x="8" y="24" width="48" height="8" fill="rgba(255,255,255,0.25)"/>
      <rect x="14" y="38" width="16" height="4" rx="2" fill="rgba(255,255,255,0.3)"/>
      <rect x="36" y="38" width="10" height="4" rx="2" fill="rgba(255,255,255,0.2)"/>
      <circle cx="44" cy="20" r="4" fill="rgba(255,255,255,0.2)"/>
      <circle cx="48" cy="20" r="4" fill="rgba(255,255,255,0.15)"/>
    </svg>
  ),
};

const colourClassMap: Record<string, string> = {
  orange: styles.qaOrange,
  navy: styles.qaNavy,
  turquoise: styles.qaTurquoise,
  green: styles.qaGreen,
  purple: styles.qaPurple,
  teal: styles.qaTeal,
  slate: styles.qaSlate,
  rose: styles.qaRose,
  coral: styles.qaCoral,
  steel: styles.qaSteel,
};

const moduleIconBg: Record<string, string> = {
  orange: 'linear-gradient(135deg, #FF8300, #FF9A33)',
  turquoise: 'linear-gradient(135deg, #00B0B9, #2DCCD3)',
  green: 'linear-gradient(135deg, #00AA6E, #00C47E)',
  purple: 'linear-gradient(135deg, #7C3AED, #9556F7)',
  navy: 'linear-gradient(135deg, #0A2240, #143A62)',
  teal: 'linear-gradient(135deg, #0D9488, #14B8A6)',
  slate: 'linear-gradient(135deg, #475569, #64748B)',
  rose: 'linear-gradient(135deg, #E11D48, #FB7185)',
  steel: 'linear-gradient(135deg, #b45309, #d97706)',
  blue: 'linear-gradient(135deg, #2563EB, #3B82F6)',
};

const moduleAbbrev: Record<string, string> = {
  onboarding: 'On',
  'employee-records': 'Em',
  leave: 'Lv',
  appraisals: 'Ap',
  admin: 'Ad',
};

const colourHex: Record<string, string> = {
  orange: '#FF8300',
  turquoise: '#00B0B9',
  green: '#00AA6E',
  purple: '#7C3AED',
  navy: '#0A2240',
  teal: '#0D9488',
  slate: '#475569',
  rose: '#E11D48',
  steel: '#b45309',
  blue: '#2563EB',
};

// Quick-action background overrides for colours not in the CSS .qa* palette.
// Applied inline (flat solid) the same way module cards colour themselves.
const qaColourHex: Record<string, string> = {
  blue: '#2563EB',
  steel: '#92400E',
};

interface CelebrationItem {
  employeeId: string;
  fullName: string;
  jobTitle: string;
  office: string;
  type: string;        // "Birthday" | "Anniversary"
  date: string;        // yyyy-MM-dd (next occurrence)
  daysUntil: number;
  years: number | null;
}

function celebrationWhen(c: CelebrationItem): string {
  const d = new Date(c.date);
  const dateLabel = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  if (c.daysUntil <= 0) return `Today (${dateLabel})`;
  if (c.daysUntil === 1) return `Tomorrow (${dateLabel})`;
  return `in ${c.daysUntil} days (${dateLabel})`;
}

function getTimeGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function formatDate(): string {
  return new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function HomePage() {
  const navigate = useNavigate();
  const { userName, userEmail, logout } = useAuth();
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [complianceOutstanding, setComplianceOutstanding] = useState<ComplianceOutstandingItem[]>([]);
  const [celebrations, setCelebrations] = useState<CelebrationItem[]>([]);
  const [celebrationsOpen, setCelebrationsOpen] = useState(false);

  const initials = userName
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  useEffect(() => {
    dashboardApi
      .getDashboard()
      .then(setData)
      .catch((err) => setError(err.message || 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    complianceApi
      .getMyOutstanding()
      .then(setComplianceOutstanding)
      .catch((err) => console.warn('Failed to load compliance outstanding', err));
  }, []);

  useEffect(() => {
    api
      .get<CelebrationItem[]>('/api/dashboard/celebrations?days=30')
      .then(setCelebrations)
      .catch((err) => console.warn('Failed to load celebrations', err));
  }, []);

  if (loading) {
    return <div className={styles.pageWrap}><div className={styles.loading}>Loading your dashboard...</div></div>;
  }

  if (error || !data) {
    return <div className={styles.pageWrap}><div className={styles.error}>{error || 'Something went wrong.'}</div></div>;
  }

  return (
    <div className={styles.pageWrap}>
      <div className={styles.homeContainer}>

        {/* ── Zone 1: Welcome Banner with Logo ── */}
        <div className={styles.banner}>
          <div className={`${styles.bannerOrb} ${styles.bannerOrb1}`} />
          <div className={`${styles.bannerOrb} ${styles.bannerOrb2}`} />
          <div className={`${styles.bannerOrb} ${styles.bannerOrb3}`} />

          {/* Top row: logo left, user right */}
          <div className={styles.bannerTop}>
            <div className={styles.bannerLogo}>
              {/* Official Origin logo — Flow Paths (on dark variant) */}
              <svg width="36" height="36" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 44 C8 32 16 20 26 14 C34 9 42 10 48 18" fill="none" stroke="#00B0B9" strokeWidth="5.5" strokeLinecap="round"/>
                <path d="M8 6 C12 18 20 30 30 36 C38 41 46 40 50 32" fill="none" stroke="#FF8300" strokeWidth="5.5" strokeLinecap="round"/>
                <circle cx="26" cy="25" r="4.5" fill="#FFFFFF"/>
              </svg>
              <span className={styles.bannerLogoText}>Origin</span>
            </div>
            <div className={styles.bannerUser}>
              <div className={styles.bannerUserInfo}>
                <span className={styles.bannerUserName}>{userName}</span>
                <span className={styles.bannerUserEmail}>{userEmail}</span>
              </div>
              <div className={styles.bannerAvatar}>{initials}</div>
              <button className={styles.bannerLogout} onClick={logout} title="Sign out">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Greeting */}
          <div className={styles.greeting}>
            {getTimeGreeting()}, <span className={styles.greetingName}>{data.greeting.firstName}</span>
          </div>
          <div className={styles.greetingSub}>
            {data.greeting.pendingActionCount > 0
              ? `You have ${data.greeting.pendingActionCount} item${data.greeting.pendingActionCount !== 1 ? 's' : ''} that need your attention`
              : "You're all caught up!"}
          </div>
          <div className={styles.datePill}>{formatDate()}</div>
        </div>

        {/* ── Zone 2: Quick Actions ── */}
        <div className={styles.sectionLabel}>Quick actions</div>
        <div className={styles.quickActionsGrid}>
          {data.quickActions.map((qa: DashboardQuickAction) => (
            <button
              key={qa.id}
              className={`${styles.quickAction} ${qaColourHex[qa.colour] ? '' : (colourClassMap[qa.colour] || styles.qaNavy)}`}
              style={qaColourHex[qa.colour] ? { background: qaColourHex[qa.colour] } : undefined}
              onClick={() => navigate(qa.route)}
            >
              <div className={styles.qaShine} />
              <div className={styles.qaShine2} />
              {qa.badgeCount > 0 && <div className={styles.qaBadge}>{qa.badgeCount}</div>}
              <div className={styles.qaIcon}>{qaIcons[qa.id] || qaIcons['my-requests']}</div>
              <div className={styles.qaLabel}>{qa.label}</div>
              <div className={styles.qaSubtitle}>{qa.subtitle}</div>
            </button>
          ))}
        </div>

        {/* ── Celebrations: upcoming birthdays & anniversaries for your downline ── */}
        {celebrations.length > 0 && (
          <div className={styles.celebrationsCard}>
            <div className={styles.celebrationsHeader} onClick={() => setCelebrationsOpen((o) => !o)}>
              <div className={styles.celebrationsHeaderLeft}>
                <span style={{ fontSize: 16 }}>🎉</span>
                <span className={styles.celebrationsTitle}>Upcoming celebrations</span>
                <span className={styles.celebrationsCount}>{celebrations.length}</span>
              </div>
              <span className={`${styles.celebrationsChevron} ${celebrationsOpen ? styles.celebrationsChevronOpen : ''}`}>▼</span>
            </div>
            {celebrationsOpen && (
              <div className={styles.celebrationsList}>
                {celebrations.map((c) => (
                  <div key={`${c.type}-${c.employeeId}-${c.date}`} className={styles.celebrationRow}>
                    <span className={styles.celebrationEmoji}>{c.type === 'Anniversary' ? '🎉' : '🎂'}</span>
                    <div className={styles.celebrationInfo}>
                      <div className={styles.celebrationName}>{c.fullName}</div>
                      <div className={styles.celebrationMeta}>
                        {c.type === 'Anniversary' ? `${c.years}-year work anniversary` : 'Birthday'}
                      </div>
                    </div>
                    <span className={styles.celebrationWhen}>{celebrationWhen(c)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Zone 3: Needs Attention ── */}
        <div className={styles.attentionSection}>
          <div className={styles.attentionHeader}>
            <div className={styles.attentionHeaderLeft}>
              <span className={styles.sectionLabel} style={{ margin: 0 }}>Needs attention</span>
              {(data.needsAttention.length + complianceOutstanding.length) > 0 && (
                <span className={styles.attentionCount}>{data.needsAttention.length + complianceOutstanding.length}</span>
              )}
            </div>
            {data.needsAttention.length > 0 && (
              <button className={styles.attentionViewAll} onClick={() => navigate('/requests')}>
                View all
              </button>
            )}
          </div>
          <div className={styles.attentionScroll}>
            {(data.needsAttention.length + complianceOutstanding.length) === 0 ? (
              <div className={styles.emptyAttention}>
                <span className={styles.emptyCheckmark}>&#10003;</span>
                You're all caught up! No items need your attention.
              </div>
            ) : (
              <>
                {complianceOutstanding.map((c: ComplianceOutstandingItem) => (
                  <div
                    key={`compliance-${c.complianceAssignmentId}`}
                    className={styles.attentionCard}
                    onClick={() => navigate(`/compliance/attest/${c.complianceItemId}`)}
                  >
                    <span className={styles.categoryBadge} style={{ background: '#16A34A', color: '#fff' }}>
                      Compliance
                    </span>
                    <div className={styles.attentionName}>{c.title}</div>
                    <div className={styles.attentionDetail}>Action required &mdash; please complete</div>
                    <div className={styles.attentionActions}>
                      <button
                        className={styles.btnReview}
                        onClick={(e) => { e.stopPropagation(); navigate(`/compliance/attest/${c.complianceItemId}`); }}
                      >
                        Complete
                      </button>
                    </div>
                  </div>
                ))}
                {data.needsAttention.map((item: DashboardAttentionItem) => (
                <div
                  key={`${item.entityType}-${item.entityId}`}
                  className={styles.attentionCard}
                  onClick={() => navigate(item.route)}
                >
                  {item.urgency && (
                    <span
                      className={`${styles.urgencyBadge} ${
                        item.urgency === 'Overdue'
                          ? styles.urgencyOverdue
                          : item.urgency === 'DueSoon'
                          ? styles.urgencyDueSoon
                          : styles.urgencyOnTrack
                      }`}
                    >
                      {item.urgency === 'DueSoon' ? 'Due soon' : item.urgency === 'OnTrack' ? 'On track' : 'Overdue'}
                    </span>
                  )}
                  <span
                    className={`${styles.categoryBadge} ${
                      item.category === 'ITTask'
                        ? styles.categoryITTask
                        : item.category === 'Leave'
                        ? styles.categoryLeave
                        : item.category === 'Credit Card'
                        ? styles.categoryCreditCard
                        : styles.categoryOnboarding
                    }`}
                  >
                    {item.category === 'ITTask' ? 'IT Task' : item.category}
                  </span>
                  <div className={styles.attentionName}>{item.personName}</div>
                  <div className={styles.attentionDetail}>{item.detail}</div>
                  <div className={styles.attentionActions}>
                    {item.canApprove && (
                      <button
                        className={styles.btnApprove}
                        onClick={(e) => { e.stopPropagation(); navigate(item.route); }}
                      >
                        Approve
                      </button>
                    )}
                    <button
                      className={styles.btnReview}
                      onClick={(e) => { e.stopPropagation(); navigate(item.route); }}
                    >
                      {item.canApprove ? 'Review' : item.entityType === 'Task' ? 'Open task' : 'Details'}
                    </button>
                  </div>
                </div>
                ))}
              </>
            )}
          </div>
        </div>

        {/* ── Zone 4: Module Launcher ── */}
        <div className={styles.sectionLabel}>Modules</div>
        <div className={styles.modulesGrid}>
          {data.modules.map((mod: DashboardModule) => (
            <div
              key={mod.id}
              className={`${styles.moduleCard} ${mod.status === 'ComingSoon' ? styles.moduleComingSoon : ''}`}
              onClick={() => mod.status !== 'ComingSoon' && navigate(mod.route)}
            >
              <div className={styles.moduleGlow} style={{ background: colourHex[mod.colour] || '#0A2240' }} />
              {mod.status === 'ComingSoon' && <span className={styles.comingSoonBadge}>Coming soon</span>}
              <div className={styles.moduleHead}>
                <div className={styles.moduleIcon} style={{ background: moduleIconBg[mod.colour] || moduleIconBg.navy }}>
                  {moduleAbbrev[mod.id] || mod.name.slice(0, 2)}
                </div>
                <div className={styles.moduleTitle}>{mod.name}</div>
              </div>
              <div className={styles.moduleDesc}>{mod.description}</div>
              <div className={styles.moduleStats}>
                {mod.stats.map((stat) => (
                  <div key={stat.label} className={styles.moduleStat}>
                    <span className={`${styles.moduleStatValue} ${stat.isHighlighted ? styles.moduleStatValueHighlight : ''}`}>
                      {stat.value}
                    </span>
                    <span className={styles.moduleStatLabel}>{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* ── Zone 5: Company Snapshot ── */}
        <div className={styles.sectionLabel}>Snapshot</div>
        <div className={styles.snapshotGrid}>
          {data.snapshot.map((snap: DashboardSnapshot) => (
            <div key={snap.label} className={styles.snapshotTile}>
              <div className={styles.snapshotBg} style={{ background: colourHex[snap.colour] || '#0A2240' }} />
              <div className={styles.snapshotLabel}>{snap.label}</div>
              <div className={styles.snapshotValue}>{snap.value}</div>
              <div className={`${styles.snapshotTrend} ${
                snap.trendDirection === 'Up' ? styles.trendUp
                : snap.trendDirection === 'Down' ? styles.trendDown
                : styles.trendNeutral
              }`}>
                {snap.trend}
              </div>
            </div>
          ))}
        </div>

      </div>
      <HelpChatWidget />
    </div>
  );
}
